import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Play,
  Pause,
  SkipForward,
  Radio as RadioIcon,
  Music2,
  Mic2,
  Video as VideoIcon,
  Loader2,
} from "lucide-react";
import { getRadioFeed } from "@/lib/api/radio.functions";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioSocial } from "@/components/site/RadioSocial";
import { ServicesExplainerSlides } from "@/components/radio/ServicesExplainerSlides";
import explainerMp4 from "@/assets/ndlcs-services-explainer.mp4.asset.json";

export const Route = createFileRoute("/radio")({
  head: () => {
    const canonical = "https://nkadimengdannylcs.com/radio";
    const ogImage = "https://nkadimengdannylcs.com/og/radio-default.jpg";
    const description =
      "Listen to NDL Justice Radio: short legal-justice talk sessions on pressing South African issues, mixed with music. Free to stream and share.";
    return {
      meta: [
        {
          title:
            "NDL Justice Radio — Talk + music | Nkadimeng Danny Legal & Compliance",
        },
        { name: "description", content: description },
        { property: "og:title", content: "NDL Justice Radio" },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:site_name", content: "NDL Justice Radio" },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: "NDL Justice Radio cover" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "NDL Justice Radio" },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  errorComponent: ({ error }) => (
    <div className="container-narrow py-20 text-center text-sm text-red-600">
      Radio is offline: {error.message}
    </div>
  ),
  notFoundComponent: () => (
    <div className="container-narrow py-20 text-center">Not found</div>
  ),
  component: RadioPage,
});

type Item = {
  id: string;
  rawId: string;
  slug?: string | null;
  kind: "episode" | "track" | "video";
  title: string;
  subtitle: string;
  url: string;
  poster?: string | null;
};

function RadioPage() {
  const fetchFeed = useServerFn(getRadioFeed);
  const { data, isLoading } = useQuery({
    queryKey: ["radio-feed"],
    queryFn: () => fetchFeed(),
    refetchInterval: 5 * 60_000,
  });

  const queue: Item[] = useMemo(() => {
    const items: Item[] = [];
    for (const e of data?.episodes ?? []) {
      if (e.audio_signed_url) {
        items.push({
          id: `ep-${e.id}`,
          rawId: e.id,
          slug: (e as any).slug ?? e.id,
          kind: "episode",
          title: e.title,
          subtitle: e.topic,
          url: e.audio_signed_url,
        });
      }
    }
    for (const t of data?.tracks ?? []) {
      if (!t.audio_signed_url) continue;
      const isVideo = (t as any).media_kind === "video";
      items.push({
        id: `tr-${t.id}`,
        rawId: t.id,
        kind: isVideo ? "video" : "track",
        title: t.title,
        subtitle: t.artist || (isVideo ? "Video" : "Music"),
        url: t.audio_signed_url,
        poster: (t as any).thumbnail_signed_url ?? null,
      });
    }
    // Audio player skips videos; videos are shown in the Video tab.
    return items;
  }, [data]);

  const audioQueue = useMemo(() => queue.filter((q) => q.kind !== "video"), [queue]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);

  const current = audioQueue[idx];

  useEffect(() => {
    if (!audioRef.current || !current) return;
    audioRef.current.src = current.url;
    if (playing) audioRef.current.play().catch(() => setPlaying(false));
  }, [current?.url]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.play().catch(() => setPlaying(false));
    else audioRef.current.pause();
  }, [playing]);

  function next() {
    if (audioQueue.length === 0) return;
    setIdx((i) => (i + 1) % audioQueue.length);
    setPlaying(true);
  }

  function jumpTo(i: number) {
    setIdx(i);
    setPlaying(true);
  }

  const latestEpisode = data?.episodes?.[0];

  return (
    <div className="bg-background">
      <section className="bg-gradient-to-b from-navy/10 to-transparent py-14">
        <div className="container-narrow">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            Now broadcasting
          </p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            NDL Justice Radio
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            Short talk-radio sessions on the real legal and access-to-justice
            issues South Africans face, with practical steps you can take
            today — mixed with music between segments. Free to stream.
          </p>

          {isLoading ? (
            <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Tuning in…
            </div>
          ) : audioQueue.length === 0 ? (
            <div className="mt-8 rounded-xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
              We're off-air right now. New sessions and music drop weekly —
              check back soon.
            </div>
          ) : (
            <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-soft">
              <div className="flex items-start gap-4">
                <div className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-navy text-navy-foreground">
                  {current?.kind === "episode" ? (
                    <Mic2 className="h-6 w-6" />
                  ) : (
                    <Music2 className="h-6 w-6" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs uppercase tracking-widest text-muted-foreground">
                    {current?.kind === "episode" ? "Talk session" : "Music"} · Now playing
                  </p>
                  {current?.kind === "episode" ? (
                    <Link
                      to="/radio/$slug"
                      params={{ slug: current.slug ?? current.id.replace(/^ep-/, "") }}
                      className="block truncate font-display text-xl text-navy hover:text-gold"
                    >
                      {current?.title ?? "—"}
                    </Link>
                  ) : (
                    <h2 className="truncate font-display text-xl text-navy">
                      {current?.title ?? "—"}
                    </h2>
                  )}
                  <p className="truncate text-sm text-muted-foreground">
                    {current?.subtitle ?? ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    onClick={() => setPlaying((p) => !p)}
                    className="bg-navy hover:bg-navy/90"
                    size="lg"
                  >
                    {playing ? (
                      <>
                        <Pause className="mr-2 h-4 w-4" /> Pause
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" /> Play
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={next} aria-label="Skip">
                    <SkipForward className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <audio
                ref={audioRef}
                onEnded={next}
                preload="auto"
                className="mt-4 w-full"
                controls
              />
            </div>
          )}
        </div>
      </section>

      <section className="py-8">
        <div className="container-narrow">
          <div className="mb-3 flex items-baseline justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold">
                Featured explainer
              </p>
              <h2 className="mt-1 font-display text-2xl text-navy">
                Our services in 5 minutes
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Meet Kabelo and Naledi as they walk you through every service
                we offer — from labour and CCMA support to company
                registration, contracts, POPIA and training.
              </p>
            </div>
            <a
              href={explainerMp4.url}
              download="ndlcs-services-explainer.mp4"
              className="inline-flex items-center gap-2 self-start rounded-md border border-gold/40 bg-gold/10 px-3 py-2 text-xs font-medium text-navy hover:bg-gold/20"
            >
              <VideoIcon className="h-3.5 w-3.5" /> Download narrated MP4
            </a>
          </div>
          <ServicesExplainerSlides />
        </div>
      </section>

      <section className="py-10">
        <div className="container-narrow">
          <Tabs defaultValue="queue">
            <TabsList>
              <TabsTrigger value="queue">
                <RadioIcon className="mr-2 h-3.5 w-3.5" /> Up next
              </TabsTrigger>
              <TabsTrigger value="video">
                <VideoIcon className="mr-2 h-3.5 w-3.5" /> Video
              </TabsTrigger>
              <TabsTrigger value="episode">
                <Mic2 className="mr-2 h-3.5 w-3.5" /> Latest session
              </TabsTrigger>
            </TabsList>

            <TabsContent value="queue" className="mt-6">
              <ul className="divide-y divide-border rounded-xl border border-border bg-card">
                {audioQueue.map((it, i) => (
                  <li
                    key={it.id}
                    className={`flex flex-col gap-3 p-4 ${
                      i === idx ? "bg-navy/5" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => jumpTo(i)}
                        className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-navy text-navy-foreground hover:bg-navy/90"
                        aria-label={`Play ${it.title}`}
                      >
                        <Play className="h-4 w-4" />
                      </button>
                      <div className="min-w-0 flex-1">
                        {it.kind === "episode" ? (
                          <Link
                            to="/radio/$slug"
                            params={{ slug: it.slug ?? it.id.replace(/^ep-/, "") }}
                            className="block truncate font-medium text-navy hover:text-gold"
                          >
                            {it.title}
                          </Link>
                        ) : (
                          <p className="truncate font-medium text-navy">{it.title}</p>
                        )}
                        <p className="truncate text-xs text-muted-foreground">
                          {it.kind === "episode" ? "Talk · " : "Music · "}
                          {it.subtitle}
                        </p>
                      </div>
                    </div>
                    <RadioSocial
                      itemId={it.rawId}
                      itemKind={it.kind === "episode" ? "episode" : "track"}
                      shareText={
                        it.kind === "episode"
                          ? `Listen to "${it.title}" on NDL Justice Radio.`
                          : `Now playing on NDL Justice Radio: "${it.title}"${it.subtitle ? ` — ${it.subtitle}` : ""}.`
                      }
                      shareUrl={
                        it.kind === "episode"
                          ? `https://nkadimengdannylcs.com/radio/${it.slug ?? it.rawId}`
                          : `https://nkadimengdannylcs.com/radio#track-${it.rawId}`
                      }
                      compact
                    />
                  </li>
                ))}
                {audioQueue.length === 0 && (
                  <li className="p-6 text-center text-sm text-muted-foreground">
                    Nothing in the queue yet.
                  </li>
                )}
              </ul>
            </TabsContent>

            <TabsContent value="video" className="mt-6">
              {queue.filter((q) => q.kind === "video").length === 0 ? (
                <p className="rounded-xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">
                  No video sessions published yet.
                </p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {queue
                    .filter((q) => q.kind === "video")
                    .map((v) => (
                      <figure key={v.id} className="overflow-hidden rounded-xl border border-border bg-card shadow-soft">
                        <video
                          controls
                          preload="metadata"
                          playsInline
                          poster={v.poster ?? undefined}
                          className="aspect-video w-full bg-black"
                          src={v.url}
                        />

                        <figcaption className="p-3">
                          <p className="truncate font-medium text-navy">{v.title}</p>
                          <p className="truncate text-xs text-muted-foreground">{v.subtitle}</p>
                          <RadioSocial
                            itemId={v.rawId}
                            itemKind="track"
                            shareText={`Watch "${v.title}" on NDL Justice Radio.`}
                            shareUrl={`https://nkadimengdannylcs.com/radio#video-${v.rawId}`}
                            compact
                          />
                        </figcaption>
                      </figure>
                    ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="episode" className="mt-6">
              {latestEpisode ? (
                <article className="rounded-xl border border-border bg-card p-6">
                  <Link
                    to="/radio/$slug"
                    params={{ slug: (latestEpisode as any).slug ?? latestEpisode.id }}
                    className="block hover:text-gold"
                  >
                    <h2 className="font-display text-2xl text-navy">
                      {latestEpisode.title}
                    </h2>
                  </Link>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Topic: {latestEpisode.topic}
                  </p>
                  <div className="prose prose-sm mt-4 max-w-none whitespace-pre-wrap text-foreground/90">
                    {latestEpisode.script}
                  </div>
                  <Link
                    to="/radio/$slug"
                    params={{ slug: (latestEpisode as any).slug ?? latestEpisode.id }}
                    className="mt-4 inline-block text-sm text-navy underline hover:text-gold"
                  >
                    Open episode page →
                  </Link>
                </article>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No talk sessions published yet.
                </p>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
